import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Loader2, CalendarRange } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const TIME_SLOTS = ["08:30", "10:00", "11:30", "13:00", "14:30", "16:00"];

const toYMD = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fmtDate = (d, opts = {}) =>
  d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric", ...opts });

const fromYMD = (s) => new Date(s + "T00:00:00");

/** Monday..Sunday for the week containing `anchor` (+ weekOffset weeks), clamped to today. */
const weekRange = (weekOffset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dow = (today.getDay() + 6) % 7; // Mon = 0
  const monday = new Date(today);
  monday.setDate(today.getDate() - dow + weekOffset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: monday < today ? today : monday, to: sunday };
};

/** Group day-blocks created from one range into a single list entry. */
const groupBlocks = (blocks) => {
  const out = [];
  const byRange = new Map();
  for (const b of blocks) {
    if (!b.range_id) {
      out.push({ key: b.id, single: b, from: b.date, to: b.date, slots: b.slots || [], reason: b.reason });
      continue;
    }
    const g = byRange.get(b.range_id);
    if (g) {
      g.days.push(b);
      if (b.date < g.from) g.from = b.date;
      if (b.date > g.to) g.to = b.date;
    } else {
      const g2 = { key: b.range_id, rangeId: b.range_id, days: [b], from: b.date, to: b.date, slots: b.slots || [], reason: b.reason };
      byRange.set(b.range_id, g2);
      out.push(g2);
    }
  }
  return out.sort((a, b) => a.from.localeCompare(b.from));
};

/**
 * Admin panel: blackout dates & time-slots.
 * Supports blocking a single day, specific slots, or a full date range (e.g. a week off).
 */
export default function AvailabilityAdmin() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // new block form
  const [mode, setMode] = useState("single"); // "single" | "range"
  const [date, setDate] = useState(null);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [wholeDay, setWholeDay] = useState(true);
  const [slots, setSlots] = useState([]);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/availability");
      setBlocks(r.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSlot = (s) =>
    setSlots((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const rangeDays = useMemo(() => {
    if (!range.from || !range.to) return 0;
    return Math.round((range.to - range.from) / 86400000) + 1;
  }, [range]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const body = { slots: wholeDay ? [] : slots, reason: reason.trim() };
      if (mode === "range") {
        body.date = toYMD(range.from);
        body.end_date = toYMD(range.to || range.from);
      } else {
        body.date = toYMD(date);
      }
      await api.post("/admin/availability", body);
      setDate(null);
      setRange({ from: undefined, to: undefined });
      setSlots([]);
      setReason("");
      setWholeDay(true);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (group) => {
    if (group.rangeId) {
      await api.delete(`/admin/availability/range/${group.rangeId}`);
      setBlocks((b) => b.filter((x) => x.range_id !== group.rangeId));
    } else {
      await api.delete(`/admin/availability/${group.single.id}`);
      setBlocks((b) => b.filter((x) => x.id !== group.single.id));
    }
  };

  const hasDate = mode === "range" ? Boolean(range.from) : Boolean(date);
  const canSubmit = hasDate && (wholeDay || slots.length > 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const grouped = useMemo(() => groupBlocks(blocks), [blocks]);

  const dateLabel =
    mode === "range"
      ? range.from
        ? `${fmtDate(range.from, { year: undefined })} → ${range.to ? fmtDate(range.to) : "…"}`
        : "Select a start and end date"
      : date
      ? fmtDate(date)
      : "Select a date";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      {/* Left: add form */}
      <div className="lg:col-span-5">
        <p className="eyebrow mb-3">Block a Day / Week</p>
        <p className="text-neutral-400 text-sm mb-8 leading-relaxed">
          Block a single day, specific time slots, or a whole date range such as a
          week away. Customers won't be able to book any blocked slot.
        </p>

        <div className="space-y-6 border-t border-white/10 pt-6">
          <div>
            <label className="eyebrow block mb-3">Block Type</label>
            <div className="flex gap-2">
              <button
                data-testid="admin-avail-mode-single"
                onClick={() => setMode("single")}
                className={`flex-1 py-3 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                  mode === "single"
                    ? "border-white bg-white text-black"
                    : "border-white/10 hover:border-white/30 text-neutral-300"
                }`}
              >
                Single Day
              </button>
              <button
                data-testid="admin-avail-mode-range"
                onClick={() => setMode("range")}
                className={`flex-1 py-3 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                  mode === "range"
                    ? "border-white bg-white text-black"
                    : "border-white/10 hover:border-white/30 text-neutral-300"
                }`}
              >
                Date Range / Week
              </button>
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-3">{mode === "range" ? "Dates" : "Date"}</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="admin-avail-date-btn"
                  className="input-minimal text-left flex items-center justify-between hover:border-white/40 transition-colors"
                >
                  <span className={hasDate ? "text-white" : "text-white/35"}>{dateLabel}</span>
                  {mode === "range" && rangeDays > 0 && (
                    <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-500" data-testid="admin-avail-range-days">
                      {rangeDays} day{rangeDays === 1 ? "" : "s"}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-auto p-0 bg-[#0a0a0a] border border-white/10 text-white"
                data-lenis-prevent
              >
                {mode === "range" ? (
                  <Calendar
                    mode="range"
                    selected={range}
                    onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                    disabled={(d) => d < today}
                    numberOfMonths={1}
                    initialFocus
                  />
                ) : (
                  <Calendar
                    mode="single"
                    selected={date || undefined}
                    onSelect={(d) => d && setDate(d)}
                    disabled={(d) => d < today}
                    initialFocus
                  />
                )}
              </PopoverContent>
            </Popover>

            {mode === "range" && (
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { label: "Rest of this week", offset: 0, testid: "this-week" },
                  { label: "Next week", offset: 1, testid: "next-week" },
                  { label: "Week after", offset: 2, testid: "week-after" },
                ].map((q) => (
                  <button
                    key={q.testid}
                    type="button"
                    data-testid={`admin-avail-quick-${q.testid}`}
                    onClick={() => setRange(weekRange(q.offset))}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-white/40 text-neutral-300 font-mono text-[10px] tracking-widest uppercase transition-colors"
                  >
                    <CalendarRange size={11} strokeWidth={1.6} />
                    {q.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="eyebrow block mb-3">Scope</label>
            <div className="flex gap-2">
              <button
                data-testid="admin-avail-whole-day"
                onClick={() => setWholeDay(true)}
                className={`flex-1 py-3 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                  wholeDay
                    ? "border-white bg-white text-black"
                    : "border-white/10 hover:border-white/30 text-neutral-300"
                }`}
              >
                {mode === "range" ? "Whole Days" : "Whole Day"}
              </button>
              <button
                data-testid="admin-avail-slots"
                onClick={() => setWholeDay(false)}
                className={`flex-1 py-3 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                  !wholeDay
                    ? "border-white bg-white text-black"
                    : "border-white/10 hover:border-white/30 text-neutral-300"
                }`}
              >
                Specific Slots
              </button>
            </div>
          </div>

          {!wholeDay && (
            <div>
              <label className="eyebrow block mb-3">
                Slots to Block{mode === "range" ? " (each day)" : ""}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {TIME_SLOTS.map((s) => (
                  <button
                    key={s}
                    data-testid={`admin-avail-slot-${s}`}
                    onClick={() => toggleSlot(s)}
                    className={`py-3 border font-mono text-xs transition-all ${
                      slots.includes(s)
                        ? "border-brand-red bg-brand-red text-white"
                        : "border-white/10 hover:border-white/30 text-neutral-300"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="eyebrow block mb-3">Reason (optional)</label>
            <input
              data-testid="admin-avail-reason"
              className="input-minimal"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={mode === "range" ? "e.g. Holiday, away for the week" : "e.g. Public holiday, fully booked"}
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm font-mono" data-testid="admin-avail-error">
              {error}
            </p>
          )}

          <button
            onClick={submit}
            disabled={!canSubmit || saving}
            data-testid="admin-avail-save"
            className={`btn-primary w-full justify-center ${!canSubmit ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <Plus size={14} />
            {saving
              ? "Saving…"
              : mode === "range" && rangeDays > 1
              ? `Block ${rangeDays} Days`
              : "Block Selection"}
          </button>
        </div>
      </div>

      {/* Right: existing blocks */}
      <div className="lg:col-span-7">
        <p className="eyebrow mb-3">
          Current Blackouts {blocks.length ? `· ${blocks.length} day${blocks.length === 1 ? "" : "s"}` : ""}
        </p>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-neutral-500">
            <Loader2 className="animate-spin mr-3" size={16} />
            <span className="font-mono text-xs tracking-widest uppercase">Loading…</span>
          </div>
        ) : grouped.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-white/10">
            <p className="font-serif text-2xl text-neutral-500">No blocked dates yet.</p>
            <p className="mt-2 text-neutral-600 text-sm">Every day is currently bookable.</p>
          </div>
        ) : (
          <ul className="divide-y divide-white/10 border-t border-white/10">
            <AnimatePresence initial={false}>
              {grouped.map((g) => (
                <motion.li
                  key={g.key}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  data-testid={g.rangeId ? `admin-avail-range-${g.rangeId}` : `admin-avail-block-${g.single.id}`}
                  className="py-4 flex items-center gap-4"
                >
                  <div className="flex-1">
                    {g.rangeId ? (
                      <p className="font-serif text-xl text-white">
                        {fmtDate(fromYMD(g.from), { year: undefined })}
                        <span className="text-neutral-500 mx-2">→</span>
                        {fmtDate(fromYMD(g.to))}
                      </p>
                    ) : (
                      <p className="font-serif text-xl text-white">{fmtDate(fromYMD(g.from))}</p>
                    )}
                    <p className="text-neutral-500 text-xs mt-1">
                      {g.rangeId && (
                        <span className="inline-block mr-2 px-1.5 py-0.5 border border-white/15 font-mono text-[9px] tracking-widest uppercase text-neutral-300">
                          {g.days.length} days
                        </span>
                      )}
                      {g.slots.length === 0
                        ? g.rangeId
                          ? "Whole days blocked"
                          : "Whole day blocked"
                        : `Blocked slots: ${g.slots.join(", ")}`}
                      {g.reason ? ` · ${g.reason}` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(g)}
                    data-testid={g.rangeId ? `admin-avail-remove-range-${g.rangeId}` : `admin-avail-remove-${g.single.id}`}
                    className="text-neutral-500 hover:text-brand-red transition-colors p-2"
                    aria-label={g.rangeId ? "Remove blocked range" : "Remove blackout"}
                  >
                    <Trash2 size={16} strokeWidth={1.4} />
                  </button>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
}
